import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bot,
  CheckCircle2,
  Clock,
  Copy,
  Link2,
  MessageSquare,
  Play,
  RefreshCw,
  Send,
  Square,
  Timer,
  Unlink,
  Users,
} from "lucide-react";
import API from "../services/api";
import AppPageLoader from "./AppPageLoader";
import PageModal from "./PageModal";
import RadioButtonGroup from "./RadioButtonGroup";
import { useToast } from "./Toast";
import { formatAppDateTime } from "../utils/dateFormat";
import { getApiErrorMessage, getApiPayloadMessage } from "../utils/apiErrors";

const CONNECT_TARGET_OPTIONS = [
  { value: "private", label: "Chat", icon: MessageSquare },
  { value: "group", label: "Group", icon: Users },
];
const MIN_INITIAL_LOADER_MS = 250;

const ACTION_REASON_META = {
  sent: {
    tone: "success",
    title: "Telegram report delivered",
    message: "Telegram reporting completed successfully.",
  },
  no_active_account_sets: {
    tone: "info",
    title: "No active account sets right now",
    message: "No Telegram message was sent because no active account set is open right now.",
  },
  bot_not_configured: {
    tone: "error",
    title: "Telegram bot unavailable",
    message: "Telegram bot token is not configured on the server.",
  },
  not_verified: {
    tone: "error",
    title: "Telegram not connected",
    message: "Connect Telegram before trying to send or start reports.",
  },
  telegram_target_unsupported: {
    tone: "warning",
    title: "Telegram target unsupported",
    message: "Reconnect Telegram using Chat or Group / Supergroup.",
  },
  user_inactive: {
    tone: "warning",
    title: "Account inactive",
    message: "Telegram reporting stopped because this account is inactive.",
  },
  no_reportable_account_sets: {
    tone: "warning",
    title: "No reportable account sets",
    message: "There are no reportable account sets available for Telegram reporting.",
  },
  telegram_destination_unavailable: {
    tone: "warning",
    title: "Telegram destination unavailable",
    message: "The Telegram destination is unavailable. Reconnect Telegram before trying again.",
  },
  send_failed: {
    tone: "error",
    title: "Telegram request failed",
    message: "Telegram could not send the report right now.",
  },
};

const STOPPED_REASON_META = {
  manual_stop: {
    tone: "info",
    title: "Service stopped",
    message: "Telegram reporting was stopped manually.",
  },
  unlinked: {
    tone: "info",
    title: "Telegram disconnected",
    message: "The Telegram destination was removed from this account.",
  },
  no_reportable_account_sets: {
    tone: "warning",
    title: "No reportable account sets",
    message: "Telegram reporting stopped because no reportable account sets remain.",
  },
  user_inactive: {
    tone: "warning",
    title: "Account inactive",
    message: "Telegram reporting stopped because this account is inactive.",
  },
  telegram_destination_unavailable: {
    tone: "warning",
    title: "Telegram destination unavailable",
    message: "Telegram reporting stopped because the destination can no longer receive messages.",
  },
  telegram_target_unsupported: {
    tone: "warning",
    title: "Unsupported Telegram target",
    message:
      "This Telegram target is no longer supported. Reconnect using Chat or Group / Supergroup.",
  },
};

const PASSIVE_STOPPED_REASON_TOAST_BLOCKLIST = new Set(["unlinked"]);

function normalizeTargetType(value) {
  return String(value || "").toLowerCase() === "group" ||
    String(value || "").toLowerCase() === "supergroup"
    ? "group"
    : "private";
}

function formatInterval(ms) {
  const value = Number(ms || 0);
  if (!Number.isFinite(value) || value <= 0) return "Not set";
  if (value % 3600000 === 0) return `${value / 3600000} hr`;
  if (value % 60000 === 0) return `${value / 60000} min`;
  if (value % 1000 === 0) return `${value / 1000} sec`;
  return `${value} ms`;
}

function formatDate(value, fallback = "Never") {
  return value ? formatAppDateTime(value, fallback) : fallback;
}

function formatSecondsInput(ms) {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return "";
  const seconds = value / 1000;
  return Number.isInteger(seconds) ? String(seconds) : String(Number(seconds.toFixed(3)));
}

function formatAlertDateTime(value, fallback = "Never") {
  if (!value) return fallback;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  const dateLabel = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace("am", "AM")
    .replace("pm", "PM");

  return `${dateLabel} ${timeLabel}`;
}

function formatChatType(value) {
  const chatType = String(value || "").toLowerCase();
  if (chatType === "supergroup") return "Supergroup";
  if (chatType === "group") return "Group";
  if (chatType === "private") return "Chat";
  return "Unknown";
}

function toObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function pickFirstText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return "";
}

function getConnectGuidePreview(targetType, botUsername) {
  const botLabel = botUsername ? `@${botUsername}` : "the bot";

  if (targetType === "group") {
    return [
      "Choose the group from Telegram, then wait for verification.",
      "If needed, send the shown /start command in that group.",
    ];
  }

  return [`Press Start in ${botLabel}, then wait for confirmation.`];
}

function getConnectGuideContent(targetType, botUsername) {
  const botLabel = botUsername ? `@${botUsername}` : "the bot";

  if (targetType === "group") {
    return {
      kicker: "Group flow",
      title: "Connect a Telegram group",
      description: `Reports will post in the selected group or supergroup with ${botLabel}.`,
      targetLabel: "Group / Supergroup",
      targetNote:
        "Select this when your team monitors reports inside a shared Telegram group or supergroup.",
    };
  }

  return {
    kicker: "Chat flow",
    title: "Connect a Telegram chat",
    description: `Reports will arrive in your private chat with ${botLabel}.`,
    targetLabel: "Chat",
    targetNote:
      "Select this when reports should arrive directly in your private Telegram conversation.",
  };
}

function getRemainingSeconds(expiresAt, nowMs = Date.now()) {
  const expiryMs = expiresAt ? new Date(expiresAt).getTime() : NaN;
  if (!Number.isFinite(expiryMs)) return null;
  return Math.max(0, Math.ceil((expiryMs - nowMs) / 1000));
}

function normalizeTelegramUsername(value) {
  const username = pickFirstText(value);
  if (!username) return "";
  return `@${username.replace(/^@/, "")}`;
}

function buildTelegramIdentity({ firstName, lastName, displayName, title, name, username, id }) {
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const normalizedDisplayName = pickFirstText(displayName, title, name, fullName);
  const normalizedUsername = normalizeTelegramUsername(username);
  const rawId = pickFirstText(id);
  const idLabel = rawId || "";

  return {
    displayName: normalizedDisplayName,
    username: normalizedUsername,
    rawId,
    idLabel,
    primaryLabel: normalizedDisplayName || normalizedUsername || idLabel,
  };
}

function getDestinationIdentity(setting = {}) {
  return buildTelegramIdentity({
    firstName: pickFirstText(setting.chatFirstName),
    lastName: pickFirstText(setting.chatLastName),
    displayName: pickFirstText(setting.chatDisplayName),
    title: pickFirstText(setting.chatTitle),
    username: pickFirstText(setting.chatUsername),
    id: pickFirstText(setting.chatId),
  });
}

function getConnectedByIdentity(setting = {}) {
  const telegramUser = toObject(setting.telegramUser);

  return buildTelegramIdentity({
    firstName: pickFirstText(
      setting.connectedByFirstName,
      telegramUser.first_name,
      telegramUser.firstName,
    ),
    lastName: pickFirstText(
      setting.connectedByLastName,
      telegramUser.last_name,
      telegramUser.lastName,
    ),
    displayName: pickFirstText(
      setting.connectedByDisplayName,
      telegramUser.display_name,
      telegramUser.displayName,
    ),
    title: pickFirstText(setting.connectedByTitle, telegramUser.title),
    name: pickFirstText(setting.connectedByName, telegramUser.name),
    username: pickFirstText(
      setting.connectedByUsername,
      telegramUser.username,
      telegramUser.userName,
    ),
    id: pickFirstText(
      setting.connectedByUserId,
      setting.connectedById,
      telegramUser.id,
      telegramUser.user_id,
      telegramUser.userId,
      telegramUser.chat_id,
      telegramUser.chatId,
    ),
  });
}

function sortBrokers(brokers = []) {
  return [...brokers].sort(
    (left, right) => Number(left?.position || 0) - Number(right?.position || 0),
  );
}

function buildActionFeedback(action, payload, fallbackMessage) {
  const reason = payload?.result?.reason || "";
  const base = ACTION_REASON_META[reason] || {
    tone: payload?.success === false ? "error" : "success",
    title: action === "start" ? "Telegram reporting updated" : "Telegram update processed",
    message: fallbackMessage || "Telegram request completed.",
  };

  const message =
    getApiPayloadMessage(payload) ||
    payload?.result?.errorDescription ||
    payload?.message ||
    payload?.error ||
    base.message;

  if (!reason && !message) return null;

  if (reason === "sent" && payload?.result?.sentMessages > 0) {
    return {
      tone: base.tone,
      title: base.title,
      message: `${message} ${payload.result.sentMessages} message${payload.result.sentMessages === 1 ? "" : "s"} sent.`,
    };
  }

  if (reason === "no_active_account_sets" && action === "start") {
    return {
      tone: base.tone,
      title: "Service started",
      message: "Telegram reporting started, but no active account set is open right now.",
    };
  }

  return {
    tone: base.tone,
    title: base.title,
    message,
  };
}

function showFeedbackToast(feedback, toast) {
  if (!feedback?.message) return;
  const toastMethod = toast[feedback.tone] || toast.info;
  toastMethod(feedback.message);
}

export default function UserTelegramControls({ onInitialLoadStateChange }) {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionBusy, setActionBusy] = useState(null);
  const [intervalDraft, setIntervalDraft] = useState("");
  const [intervalModalOpen, setIntervalModalOpen] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [connectTarget, setConnectTarget] = useState("private");
  const [connectTargetDirty, setConnectTargetDirty] = useState(false);
  const [connectGuide, setConnectGuide] = useState(null);
  const [copiedGuideField, setCopiedGuideField] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const autoStartAfterLinkRef = useRef(false);
  const statusToastCacheRef = useRef({
    botMissing: "",
    lastError: "",
    stoppedReason: "",
    loadError: "",
  });

  const syncStatus = useCallback(
    (nextStatus) => {
      setStatus(nextStatus || null);
      setLoadError("");

      const nextInterval = nextStatus?.setting?.intervalMs ?? nextStatus?.defaultIntervalMs ?? "";
      setIntervalDraft(formatSecondsInput(nextInterval));

      if (!connectTargetDirty) {
        const rawTarget =
          nextStatus?.setting?.connectTargetType || nextStatus?.setting?.chatType || "private";
        setConnectTarget(normalizeTargetType(rawTarget));
      }
    },
    [connectTargetDirty],
  );

  const syncFromPayload = useCallback(
    (payload) => {
      if (payload?.status) {
        syncStatus(payload.status);
        return true;
      }

      if (payload?.setting) {
        syncStatus(payload);
        return true;
      }

      return false;
    },
    [syncStatus],
  );

  const loadStatus = useCallback(
    async ({ silent = false, showErrorToast = false, minLoadingMs = 0 } = {}) => {
      const requestStartedAt = Date.now();
      if (!silent) setLoading(true);

      try {
        const response = await API.get("/telegram/me/status");
        syncStatus(response.data);
        return response.data;
      } catch (error) {
        const message = getApiErrorMessage(error, "Failed to load Telegram status.");
        setLoadError(message);

        if (showErrorToast) {
          toast.error(message);
        }

        return null;
      } finally {
        if (!silent) {
          const elapsed = Date.now() - requestStartedAt;
          const remaining = Math.max(0, minLoadingMs - elapsed);
          if (remaining > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remaining));
          }
          setLoading(false);
        }
      }
    },
    [syncStatus, toast],
  );

  useEffect(() => {
    let active = true;

    const loadInitialStatus = async () => {
      const data = await loadStatus({ minLoadingMs: MIN_INITIAL_LOADER_MS });
      if (!active || data) return;
      setLoading(false);
    };

    loadInitialStatus();

    const interval = setInterval(() => {
      loadStatus({ silent: true });
    }, 15000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [loadStatus]);

  const runAction = useCallback(
    async (key, request) => {
      setActionBusy(key);

      try {
        const response = await request();
        if (!syncFromPayload(response.data)) {
          await loadStatus({ silent: true });
        }
        return { payload: response.data, error: null };
      } catch (error) {
        const payload = error.response?.data || null;
        if (!syncFromPayload(payload)) {
          setLoadError((current) => current || getApiErrorMessage(error));
        }
        return { payload, error };
      } finally {
        setActionBusy(null);
      }
    },
    [loadStatus, syncFromPayload],
  );

  const handleConnect = async () => {
    const { payload, error } = await runAction("connect", () =>
      API.post("/telegram/me/link", { targetType: connectTarget }),
    );

    if (error) {
      toast.error(getApiErrorMessage(error, "Failed to create Telegram link."));
      return;
    }

    setConnectGuide({
      targetType: payload?.targetType || connectTarget,
      instructions: Array.isArray(payload?.instructions) ? payload.instructions : [],
      startCommand: payload?.startCommand || "",
      expiresAt: payload?.expiresAt || null,
      linkUrl: payload?.linkUrl || "",
    });
    autoStartAfterLinkRef.current = true;
    setConnectTargetDirty(false);
    toast.success("Opening Telegram verification link.");

    if (payload?.linkUrl && typeof window !== "undefined") {
      window.open(payload.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  const copyGuideValue = useCallback(
    async (value, field, successMessage) => {
      const normalizedValue = String(value || "").trim();
      if (!normalizedValue || normalizedValue.startsWith("Generated after")) {
        toast.info("Generate a fresh Telegram connection first.");
        return;
      }

      try {
        await navigator.clipboard.writeText(normalizedValue);
        setCopiedGuideField(field);
        toast.success(successMessage);
        window.setTimeout(() => {
          setCopiedGuideField((current) => (current === field ? "" : current));
        }, 1800);
      } catch (error) {
        console.error("Failed to copy Telegram guide value", error);
        toast.error("Copy failed. Please try again.");
      }
    },
    [toast],
  );

  const handleStart = async () => {
    const { payload, error } = await runAction("start", () => API.post("/telegram/me/start"));
    const feedback = buildActionFeedback(
      "start",
      payload,
      error ? getApiErrorMessage(error, "Failed to start Telegram reporting.") : "",
    );

    if (feedback) {
      showFeedbackToast(feedback, toast);
      return;
    }

    if (error) {
      toast.error(getApiErrorMessage(error, "Failed to start Telegram reporting."));
    }
  };

  const handleStop = async () => {
    const { error } = await runAction("stop", () => API.post("/telegram/me/stop"));
    if (error) {
      toast.error(getApiErrorMessage(error, "Failed to stop Telegram reporting."));
      return;
    }

    toast.success("Telegram reporting stopped.");
  };

  const handleSendNow = async () => {
    const { payload, error } = await runAction("send-now", () => API.post("/telegram/me/send-now"));
    const feedback = buildActionFeedback(
      "send-now",
      payload,
      error ? getApiErrorMessage(error, "Failed to send Telegram report.") : "",
    );

    if (feedback) {
      showFeedbackToast(feedback, toast);
      return;
    }

    if (error) {
      toast.error(getApiErrorMessage(error, "Failed to send Telegram report."));
    }
  };

  const handleSaveInterval = async () => {
    const parsedSeconds = Number(intervalDraft);
    const minInterval = Number(status?.minIntervalMs);
    const nextIntervalMs = Math.floor(parsedSeconds * 1000);

    if (!Number.isFinite(parsedSeconds) || parsedSeconds <= 0) {
      toast.error("Enter a valid interval in seconds.");
      return;
    }

    if (Number.isFinite(minInterval) && nextIntervalMs < minInterval) {
      toast.error(`Interval must be at least ${formatSecondsInput(minInterval)} sec.`);
      return;
    }

    const { error } = await runAction("interval", () =>
      API.put("/telegram/me/interval", { intervalMs: nextIntervalMs }),
    );

    if (error) {
      toast.error(getApiErrorMessage(error, "Failed to update Telegram interval."));
      return;
    }

    setIntervalModalOpen(false);
    toast.success("Telegram interval updated.");
  };

  const handleOpenIntervalModal = () => {
    const currentInterval = status?.setting?.intervalMs ?? status?.defaultIntervalMs ?? "";
    setIntervalDraft(formatSecondsInput(currentInterval));
    setIntervalModalOpen(true);
  };

  const handleRefresh = async () => {
    setActionBusy("refresh");
    const payload = await loadStatus({ showErrorToast: true });
    setActionBusy(null);

    if (payload) {
      toast.success("Telegram status refreshed.");
    }
  };

  const handleUnlink = async () => {
    const { error } = await runAction("unlink", () => API.post("/telegram/me/unlink"));
    if (error) {
      toast.error(getApiErrorMessage(error, "Failed to unlink Telegram account."));
      return;
    }

    setUnlinkModalOpen(false);
    setConnectGuide(null);
    autoStartAfterLinkRef.current = false;
    toast.success("Telegram account unlinked.");
  };

  const setting = status?.setting || {};
  const configured = Boolean(status?.configured);
  const verified = Boolean(setting.verified);
  const running = Boolean(setting.isRunning);
  const accountSets = Array.isArray(status?.eligibleAccountSets) ? status.eligibleAccountSets : [];
  const canUseReports = configured && verified;
  const normalizedBotUsername = pickFirstText(status?.botUsername).replace(/^@/, "");
  const botProfileUrl = normalizedBotUsername ? `https://t.me/${normalizedBotUsername}` : "";
  const destinationIdentity = getDestinationIdentity(setting);
  const connectedByIdentity = getConnectedByIdentity(setting);
  const connectedTypeRaw = pickFirstText(setting.chatType, setting.connectTargetType);
  const connectedTypeLabel = connectedTypeRaw ? formatChatType(connectedTypeRaw) : null;
  const destinationCardLabel = connectedTypeLabel || (connectTarget === "group" ? "Group" : "Chat");
  const connectedUsername = destinationIdentity.username || null;
  const connectedIdLabel = destinationIdentity.idLabel || null;
  const isConnectedBySameAsDestination =
    (connectedByIdentity.rawId && connectedByIdentity.rawId === destinationIdentity.rawId) ||
    (connectedByIdentity.username &&
      connectedByIdentity.username === destinationIdentity.username) ||
    (connectedByIdentity.displayName &&
      connectedByIdentity.displayName === destinationIdentity.displayName);
  const connectedBySummary = connectedByIdentity.primaryLabel
    ? connectedByIdentity.idLabel &&
      connectedByIdentity.primaryLabel !== connectedByIdentity.idLabel
      ? `${connectedByIdentity.primaryLabel} · ${connectedByIdentity.idLabel}`
      : connectedByIdentity.primaryLabel
    : "";
  const botCardSummary =
    isConnectedBySameAsDestination || !connectedBySummary
      ? "Telegram delivery bot is ready."
      : connectedBySummary;
  const connectedSummaryMeta = [
    connectedUsername && connectedUsername !== destinationIdentity.primaryLabel
      ? connectedUsername
      : "",
    connectedIdLabel && connectedIdLabel !== destinationIdentity.primaryLabel
      ? connectedIdLabel
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const activeGuide = useMemo(() => {
    const fallbackGuide = {
      targetType: connectTarget,
      instructions: getConnectGuidePreview(connectTarget, status?.botUsername),
      startCommand: "Generated after you click Link.",
      expiresAt: setting.linkTokenExpiresAt || null,
      linkUrl: "",
    };

    if (!connectGuide || connectGuide.targetType !== connectTarget) {
      return fallbackGuide;
    }

    return {
      ...fallbackGuide,
      ...connectGuide,
      expiresAt: connectGuide.expiresAt || setting.linkTokenExpiresAt || null,
    };
  }, [connectGuide, connectTarget, setting.linkTokenExpiresAt, status?.botUsername]);
  const guideContent = useMemo(
    () => getConnectGuideContent(activeGuide.targetType || connectTarget, status?.botUsername),
    [activeGuide.targetType, connectTarget, status?.botUsername],
  );
  const guideExpirySeconds = useMemo(
    () => getRemainingSeconds(activeGuide.expiresAt, nowMs),
    [activeGuide.expiresAt, nowMs],
  );
  const guideHasGeneratedCommand =
    Boolean(activeGuide.startCommand) &&
    !String(activeGuide.startCommand).startsWith("Generated after");
  const guideHasGeneratedLink = guideHasGeneratedCommand || Boolean(activeGuide.linkUrl);
  const guideLinkExpired =
    guideHasGeneratedLink && guideExpirySeconds !== null && guideExpirySeconds <= 0;
  const guideCommandAvailable = guideHasGeneratedCommand && !guideLinkExpired;
  const guideTargetType = normalizeTargetType(activeGuide.targetType || connectTarget);
  const awaitingTelegramVerification = !verified && guideHasGeneratedLink && !guideLinkExpired;
  const showGuideExpiry =
    awaitingTelegramVerification && guideExpirySeconds !== null && guideExpirySeconds > 0;
  const nextUpdateSeconds = useMemo(
    () => getRemainingSeconds(setting.nextRunAt, nowMs),
    [nowMs, setting.nextRunAt],
  );
  const showNextUpdateCountdown = verified && running && nextUpdateSeconds !== null;
  const showGuideBadge = showGuideExpiry || showNextUpdateCountdown;
  const guideLine = [guideContent.description, ...activeGuide.instructions]
    .filter(Boolean)
    .join(" ");
  const guideCommandLabel = guideCommandAvailable
    ? activeGuide.startCommand
    : "Generated after you click Link.";
  const guideExpiryLabel = showGuideExpiry ? `${guideExpirySeconds}s` : "";
  const nextUpdateCountdownLabel = showNextUpdateCountdown ? `${nextUpdateSeconds}s` : "";
  const guideSuccessTarget = connectedIdLabel || destinationIdentity.primaryLabel || "";
  const targetPickerDisabled =
    !configured ||
    verified ||
    awaitingTelegramVerification ||
    actionBusy === "connect" ||
    actionBusy === "unlink";
  const linkActionBusy = verified
    ? actionBusy === "unlink"
    : actionBusy === "connect" || awaitingTelegramVerification;
  const linkActionDisabled = verified
    ? actionBusy === "unlink"
    : !configured || actionBusy === "connect" || awaitingTelegramVerification;
  const serviceActionBusy = actionBusy === "start" || actionBusy === "stop";
  const serviceActionDisabled = running
    ? actionBusy === "stop"
    : !canUseReports || actionBusy === "start";
  const serviceActionLabel = serviceActionBusy
    ? running
      ? "Stopping..."
      : "Starting..."
    : running
      ? "Stop"
      : "Start";

  const isInitialLoading = loading && !status;

  useEffect(() => {
    const shouldTickCountdown =
      (activeGuide.expiresAt && guideHasGeneratedLink) ||
      (verified && running && setting.nextRunAt);
    if (!shouldTickCountdown) return undefined;

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [activeGuide.expiresAt, guideHasGeneratedLink, running, setting.nextRunAt, verified]);

  useEffect(() => {
    if (!guideLinkExpired) return;
    setConnectGuide(null);
    setCopiedGuideField("");
    autoStartAfterLinkRef.current = false;
  }, [guideLinkExpired]);

  useEffect(() => {
    if (!verified || (!connectGuide && !copiedGuideField)) return;
    setConnectGuide(null);
    setCopiedGuideField("");
  }, [connectGuide, copiedGuideField, verified]);

  useEffect(() => {
    if (!autoStartAfterLinkRef.current || !verified) return;

    if (running) {
      autoStartAfterLinkRef.current = false;
      return;
    }

    if (actionBusy) return;

    autoStartAfterLinkRef.current = false;

    const startAfterLink = async () => {
      const { payload, error } = await runAction("start", () => API.post("/telegram/me/start"));
      const feedback = buildActionFeedback(
        "start",
        payload,
        error ? getApiErrorMessage(error, "Failed to start Telegram reporting.") : "",
      );

      if (feedback) {
        showFeedbackToast(feedback, toast);
        return;
      }

      if (error) {
        toast.error(getApiErrorMessage(error, "Failed to start Telegram reporting."));
      }
    };

    startAfterLink();
  }, [actionBusy, runAction, running, toast, verified]);

  useEffect(() => {
    if (typeof onInitialLoadStateChange === "function") {
      onInitialLoadStateChange(isInitialLoading);
    }
  }, [isInitialLoading, onInitialLoadStateChange]);

  useEffect(() => {
    if (!loadError) {
      statusToastCacheRef.current.loadError = "";
      return;
    }

    if (statusToastCacheRef.current.loadError !== loadError) {
      statusToastCacheRef.current.loadError = loadError;
      toast.error(loadError);
    }
  }, [loadError, toast]);

  useEffect(() => {
    if (!status) return;

    if (!configured) {
      const nextKey = "bot-missing";
      if (statusToastCacheRef.current.botMissing !== nextKey) {
        statusToastCacheRef.current.botMissing = nextKey;
        toast.warning("Telegram bot token is not configured on the server right now.");
      }
    } else {
      statusToastCacheRef.current.botMissing = "";
    }

    if (setting.lastError) {
      if (statusToastCacheRef.current.lastError !== setting.lastError) {
        statusToastCacheRef.current.lastError = setting.lastError;
        toast.error(setting.lastError);
      }
    } else {
      statusToastCacheRef.current.lastError = "";
    }

    if (!running && setting.stoppedReason && STOPPED_REASON_META[setting.stoppedReason]) {
      const stoppedMeta = STOPPED_REASON_META[setting.stoppedReason];
      const nextKey = `${setting.stoppedReason}:${setting.lastError || ""}`;
      if (statusToastCacheRef.current.stoppedReason !== nextKey) {
        statusToastCacheRef.current.stoppedReason = nextKey;
        if (!PASSIVE_STOPPED_REASON_TOAST_BLOCKLIST.has(setting.stoppedReason)) {
          const toastMethod = toast[stoppedMeta.tone] || toast.info;
          toastMethod(stoppedMeta.message);
        }
      }
    } else {
      statusToastCacheRef.current.stoppedReason = "";
    }
  }, [configured, running, setting.lastError, setting.stoppedReason, status, toast]);

  if (isInitialLoading) {
    if (typeof onInitialLoadStateChange === "function") {
      return null;
    }

    return (
      <div className="telegram-hub-page-loading">
        <AppPageLoader
          label="Loading Telegram Hub"
          detail="Fetching your Telegram status, destination, and reporting controls..."
        />
      </div>
    );
  }

  if (!status && loadError) {
    return (
      <div className="telegram-hub-stack">
        <div className="network-empty telegram-hub-empty">
          <strong>Unable to load Telegram status</strong>
          <span>{loadError}</span>
        </div>

        <div className="telegram-hub-actions">
          <button
            type="button"
            className="network-btn network-btn-ghost"
            onClick={handleRefresh}
            disabled={actionBusy === "refresh"}
          >
            <RefreshCw size={15} />
            {actionBusy === "refresh" ? "Refreshing..." : "Retry"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="telegram-hub-stack">
      <div className="network-summary-grid telegram-hub-summary-grid">
        <div className="network-summary-card">
          <div className="network-summary-icon">
            <Bot size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Bot</span>
            <strong>
              {configured ? (
                normalizedBotUsername ? (
                  <a
                    href={botProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="telegram-hub-summary-link"
                    aria-label={`Open Telegram bot @${normalizedBotUsername}`}
                  >
                    @{normalizedBotUsername}
                  </a>
                ) : (
                  "Configured"
                )
              ) : (
                "Missing"
              )}
            </strong>
            <small>
              {configured ? botCardSummary : "Server bot token still needs configuration."}
            </small>
          </div>
        </div>
        <div className="network-summary-card">
          <div className="network-summary-icon">
            <CheckCircle2 size={20} />
          </div>
          <div className="network-summary-copy">
            <span>{destinationCardLabel}</span>
            <strong>
              {verified
                ? destinationIdentity.primaryLabel || connectedTypeLabel || "Verified"
                : "Not Connected"}
            </strong>
            <small>
              {verified
                ? connectedSummaryMeta || "Telegram destination verified."
                : "Connect a chat or group to receive reports."}
            </small>
          </div>
        </div>
        <div className="network-summary-card">
          <div className="network-summary-icon">
            <Play size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Service</span>
            <strong>{running ? "Running" : "Stopped"}</strong>
            <small>
              {running
                ? `Next · ${formatDate(setting.nextRunAt, "Scheduled soon")}`
                : "Reports are currently paused."}
            </small>
          </div>
        </div>
        <div className="network-summary-card">
          <div className="network-summary-icon">
            <Timer size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Interval</span>
            <strong>{formatInterval(setting.intervalMs || status?.defaultIntervalMs)}</strong>
            <small>
              {Number.isFinite(Number(status?.minIntervalMs))
                ? `Minimum allowed: ${formatInterval(status.minIntervalMs)}`
                : "Automatic report delivery cadence."}
            </small>
          </div>
        </div>
      </div>

      <section className="network-card telegram-hub-card">
        <div className="network-card-heading telegram-hub-card-heading">
          <div className="telegram-hub-card-header-inner">
            <div className="telegram-hub-card-icon-box">
              <Link2 size={17} />
            </div>
            <div className="network-card-copy">
              <div className="network-card-title telegram-hub-card-title-row">
                <span>Connect Telegram</span>
                <span
                  className={`telegram-hub-status-badge ${
                    verified ? "telegram-hub-status-badge-active" : "telegram-hub-status-badge-idle"
                  }`}
                >
                  {verified ? "Active" : "Idle"}
                </span>
              </div>
              <p>
                Pick a destination, open the Telegram link, and verify the chat before starting
                reports.
              </p>
            </div>
          </div>
        </div>

        <div
          className={
            verified
              ? "telegram-hub-verified-section"
              : `telegram-hub-guide${showGuideBadge ? " telegram-hub-guide-with-expiry" : ""}`
          }
        >
          {!verified && showGuideBadge ? (
            <span
              className={`telegram-hub-guide-expiry-badge ${
                showGuideExpiry
                  ? guideExpirySeconds <= 0
                    ? "telegram-hub-guide-expiry-badge-expired"
                    : ""
                  : "telegram-hub-guide-expiry-badge-success"
              }`}
            >
              <Clock size={14} />
              {showGuideExpiry ? guideExpiryLabel : nextUpdateCountdownLabel}
            </span>
          ) : null}

          {verified ? (
            <div className="telegram-hub-live-card" role="status" aria-live="polite">
              <div className="telegram-hub-live-header">
                <span className="telegram-hub-live-pulse-wrap" aria-hidden="true">
                  <span className="telegram-hub-live-dot" />
                </span>
                <span className="telegram-hub-live-label">Live</span>
                <span className="telegram-hub-live-sep" aria-hidden="true">
                  ·
                </span>
                <span className="telegram-hub-live-desc">Destination verified</span>
                <span className="telegram-hub-interval-chip">
                  <Clock size={11} />
                  {showNextUpdateCountdown
                    ? nextUpdateCountdownLabel
                    : formatInterval(setting.intervalMs || status?.defaultIntervalMs)}
                </span>
              </div>
              <div className="telegram-hub-linked-to">
                <span className="telegram-hub-linked-label">Linked to</span>
                <span className="telegram-hub-linked-id">
                  {guideSuccessTarget || destinationIdentity.primaryLabel || "Verified"}
                </span>
              </div>
              <div className="telegram-hub-live-meta">
                <span className="telegram-hub-live-meta-item">
                  <CheckCircle2 size={12} aria-hidden="true" />
                  <span>Connected</span>
                  <span className="telegram-hub-live-meta-value">
                    {formatAlertDateTime(setting.verifiedAt)}
                  </span>
                </span>
                <span className="telegram-hub-live-meta-item">
                  <Send size={12} aria-hidden="true" />
                  <span>Last update</span>
                  <span className="telegram-hub-live-meta-value">
                    {setting.lastSentAt ? formatAlertDateTime(setting.lastSentAt) : "Not yet sent"}
                  </span>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="telegram-hub-guide-desc-row">
                <span className="telegram-hub-guide-icon" aria-hidden="true">
                  <Bot size={13} />
                </span>
                <p className="telegram-hub-guide-copy">
                  {guideTargetType === "private" ? (
                    <>
                      Reports will arrive in your private chat with{" "}
                      {botProfileUrl ? (
                        <a
                          href={botProfileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="telegram-hub-guide-bot-link"
                          aria-label={`Open Telegram bot @${normalizedBotUsername}`}
                        >
                          @{normalizedBotUsername}
                        </a>
                      ) : (
                        "the bot"
                      )}
                      {guideCommandAvailable
                        ? ". Open the Telegram link and press Start, then wait for confirmation."
                        : ". Generate a fresh link to connect this chat."}
                    </>
                  ) : guideCommandAvailable ? (
                    guideLine
                  ) : (
                    "Generate a fresh link, choose the group from Telegram, then wait for verification."
                  )}
                </p>
              </div>

              <div className="telegram-hub-guide-command">
                <span className="telegram-hub-guide-link-label">
                  <Link2 size={10} />
                  Start Command
                </span>
                <div
                  className="telegram-hub-guide-code"
                  role="group"
                  aria-label="Telegram start command"
                >
                  <span className="telegram-hub-guide-command-value" title={guideCommandLabel}>
                    {guideCommandLabel}
                  </span>
                  <div className="telegram-hub-guide-code-actions">
                    <button
                      type="button"
                      className="telegram-hub-guide-copy-btn"
                      onClick={() =>
                        copyGuideValue(
                          activeGuide.startCommand,
                          "command",
                          "Telegram command copied.",
                        )
                      }
                      disabled={!guideCommandAvailable}
                      aria-label={
                        copiedGuideField === "command"
                          ? "Telegram command copied"
                          : "Copy Telegram command"
                      }
                      title={
                        copiedGuideField === "command"
                          ? "Telegram command copied"
                          : "Copy Telegram command"
                      }
                    >
                      {copiedGuideField === "command" ? (
                        <CheckCircle2 size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="telegram-hub-link-controls">
          <div className="telegram-hub-target-picker-wrap">
            <div className="network-field-group telegram-hub-target-picker">
              <RadioButtonGroup
                name="telegram-connect-target"
                value={connectTarget}
                onChange={(value) => {
                  setConnectTarget(value);
                  setConnectTargetDirty(true);
                }}
                options={CONNECT_TARGET_OPTIONS}
                disabled={targetPickerDisabled}
              />
            </div>
          </div>

          <div className="telegram-hub-control-buttons">
            <div className="telegram-hub-secondary-btns">
              {verified && (
                <button
                  type="button"
                  className="network-btn network-btn-ghost telegram-hub-send-action"
                  onClick={handleSendNow}
                  disabled={!canUseReports || actionBusy === "send-now"}
                >
                  <Send size={15} />
                  <span className="telegram-hub-btn-label">
                    {actionBusy === "send-now" ? "Sending…" : "Send"}
                  </span>
                </button>
              )}
              <button
                type="button"
                className="network-btn network-btn-ghost telegram-hub-interval-btn"
                onClick={handleOpenIntervalModal}
                disabled={!configured || actionBusy === "interval"}
              >
                <Timer size={15} />
                <span className="telegram-hub-btn-label">
                  {actionBusy === "interval" ? "Saving..." : "Interval"}
                </span>
              </button>
              <button
                type="button"
                className="network-btn network-btn-ghost telegram-hub-btn-icon-only"
                onClick={handleRefresh}
                disabled={actionBusy === "refresh"}
                title={actionBusy === "refresh" ? "Refreshing…" : "Refresh"}
                aria-label={actionBusy === "refresh" ? "Refreshing…" : "Refresh"}
              >
                {actionBusy === "refresh" ? (
                  <span className="network-spinner" aria-hidden="true" />
                ) : (
                  <RefreshCw size={15} />
                )}
              </button>
            </div>

            <div className="telegram-hub-btn-row-sep" aria-hidden="true" />

            <div className="telegram-hub-primary-btns">
              {verified ? (
                <button
                  type="button"
                  className="network-btn telegram-hub-btn-unlink"
                  onClick={() => setUnlinkModalOpen(true)}
                  disabled={actionBusy === "unlink"}
                >
                  {actionBusy === "unlink" ? (
                    <span className="network-spinner" aria-hidden="true" />
                  ) : (
                    <Unlink size={15} />
                  )}
                  <span className="telegram-hub-btn-label">
                    {actionBusy === "unlink" ? "Unlinking…" : "Unlink"}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="network-btn network-btn-primary telegram-hub-link-action"
                  onClick={handleConnect}
                  disabled={linkActionDisabled}
                >
                  {linkActionBusy ? (
                    <span className="network-spinner" aria-hidden="true" />
                  ) : (
                    <Link2 size={15} />
                  )}
                  <span className="telegram-hub-btn-label">
                    {linkActionBusy ? "Linking…" : "Link"}
                  </span>
                </button>
              )}
              <button
                type="button"
                className={`network-btn telegram-hub-service-action ${
                  running ? "telegram-hub-btn-stop" : "telegram-hub-btn-inverse"
                }`}
                onClick={running ? handleStop : handleStart}
                disabled={serviceActionDisabled}
              >
                {serviceActionBusy ? (
                  <span className="network-spinner" aria-hidden="true" />
                ) : running ? (
                  <Square size={15} />
                ) : (
                  <Play size={15} />
                )}
                {serviceActionLabel}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="network-card telegram-hub-card">
        <div className="network-card-heading">
          <div className="network-card-copy">
            <div className="network-card-title">
              <CheckCircle2 size={16} />
              <span>Active account sets</span>
            </div>
            <p>These account sets are currently valid candidates for Telegram reporting.</p>
          </div>
        </div>

        {accountSets.length === 0 ? (
          <div className="network-empty">
            <strong>No eligible account sets</strong>
            <span>No account sets are currently available for Telegram reporting.</span>
          </div>
        ) : (
          <div className="telegram-hub-set-table">
            <div className="telegram-hub-set-thead" aria-hidden="true">
              <div className="telegram-hub-set-count-cell">
                <strong>{accountSets.length}</strong>
                <span>{accountSets.length === 1 ? "active set" : "active sets"}</span>
              </div>
              <span className="telegram-hub-col-label">Set</span>
              <span className="telegram-hub-col-label">Symbols</span>
              <span className="telegram-hub-col-label">Accounts</span>
            </div>

            {accountSets.map((accountSet) => (
              <article key={accountSet.id} className="telegram-hub-set-row">
                <span className="telegram-hub-set-indicator" />
                <strong className="telegram-hub-set-name">
                  {accountSet.name || "Account Set"}
                </strong>
                <span className="telegram-hub-set-symbols">
                  {accountSet.futureSymbol || "Future"} / {accountSet.spotSymbol || "Spot"}
                </span>
                <div className="telegram-hub-broker-row">
                  {sortBrokers(accountSet.brokers).map((broker) => (
                    <span
                      key={broker.id}
                      className="network-pill network-pill-total telegram-hub-broker-pill"
                    >
                      {broker.position ? `#${broker.position} ` : ""}
                      {broker.brokerName || "Broker"}
                      {broker.terminal ? ` · ${broker.terminal}` : ""}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <PageModal
        open={intervalModalOpen}
        onClose={() => {
          if (actionBusy !== "interval") setIntervalModalOpen(false);
        }}
        onConfirm={handleSaveInterval}
        title="Change Telegram Interval"
        description={
          Number.isFinite(Number(status?.minIntervalMs))
            ? `Set how often Telegram reports should run. Minimum ${formatSecondsInput(status.minIntervalMs)} sec.`
            : "Set how often Telegram reports should run."
        }
        confirmLabel={actionBusy === "interval" ? "Saving..." : "Save Interval"}
        loading={actionBusy === "interval"}
        tone="default"
        icon="info"
      >
        <div className="telegram-hub-modal-fields">
          <label className="telegram-hub-modal-label">Interval (sec)</label>
          <input
            type="number"
            min={
              Number.isFinite(Number(status?.minIntervalMs))
                ? Number(formatSecondsInput(status.minIntervalMs))
                : undefined
            }
            step="any"
            className="telegram-hub-modal-input"
            value={intervalDraft}
            onChange={(event) => setIntervalDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSaveInterval();
              }
            }}
            disabled={actionBusy === "interval"}
            autoFocus
          />
        </div>
      </PageModal>

      <PageModal
        open={unlinkModalOpen}
        onClose={() => {
          if (actionBusy !== "unlink") setUnlinkModalOpen(false);
        }}
        onConfirm={handleUnlink}
        title="Unlink Telegram?"
        description="This stops Telegram reporting and removes the verified destination from your account."
        confirmLabel={actionBusy === "unlink" ? "Unlinking..." : "Unlink Telegram"}
        loading={actionBusy === "unlink"}
        tone="danger"
      />
    </div>
  );
}
